<template>
  <div class="payments-card fundraising">
    <div class="card-header-row">
      <div class="card-title-left">
        <SectionIcon name="fundraising" size="40" />
        <h3>Fundraising</h3>
      </div>
      <button class="action-chip" @click="showCreateForm = !showCreateForm">
        <i class="bi" :class="showCreateForm ? 'bi-x-lg' : 'bi-plus-lg'"></i>
        {{ showCreateForm ? "Close" : "New campaign" }}
      </button>
    </div>
    <p class="muted">
      Campaigns get a public donation page anyone can use — share the link in
      WhatsApp groups or print the QR code on a match-day poster. Money goes
      straight to the club's Stripe account.
    </p>

    <!-- Create form ------------------------------------------------------ -->
    <div class="inline-form campaign-form" v-if="showCreateForm">
      <label class="grow">
        Title
        <input type="text" v-model="form.title" placeholder="e.g. New minibus appeal" />
      </label>
      <label class="grow full">
        The story (shown on the public page)
        <textarea rows="4" v-model="form.description"
          placeholder="What are you raising money for, and what difference will it make? Avoid naming individual children."></textarea>
      </label>
      <label>
        Target ({{ currencySymbol }}, optional)
        <input type="number" min="0" step="1" v-model="form.targetPounds" />
      </label>
      <label>
        Deadline (optional)
        <input type="date" v-model="form.deadline" />
      </label>
      <label>
        Suggested amounts ({{ currencySymbol }}, comma separated)
        <input type="text" v-model="form.suggestedPounds" placeholder="5, 10, 25" />
      </label>
      <div class="visibility-block full">
        <span class="vis-title">Who sees this campaign?</span>
        <div class="vis-options">
          <label class="vis-radio">
            <input type="radio" value="all" v-model="form.visibility" /> All teams
          </label>
          <label class="vis-radio">
            <input type="radio" value="teams" v-model="form.visibility" /> Specific teams
          </label>
        </div>
        <div class="team-checks" v-if="form.visibility === 'teams'">
          <label v-for="t in teams" :key="t.id" class="team-check">
            <input type="checkbox" :value="String(t.id)" v-model="form.selectedTeamIds" />
            {{ t.teamName }}
          </label>
          <p class="vis-hint" v-if="teams.length === 0">No teams found for this club.</p>
        </div>
      </div>
      <button class="action-chip confirm" :disabled="saving" @click="saveCampaign">
        {{ saving ? "Saving…" : "Create campaign" }}
      </button>
      <p class="error-text" v-if="formError">{{ formError }}</p>
    </div>

    <!-- Campaign list ----------------------------------------------------- -->
    <p class="muted" v-if="loading">Loading campaigns…</p>
    <p class="muted" v-else-if="campaigns.length === 0">No campaigns yet.</p>

    <div class="campaign" v-for="campaign in campaigns" :key="campaign.id">
      <div class="campaign-head">
        <div>
          <h4>
            {{ campaign.title }}
            <span class="status-pill" :class="campaign.status === 'active' ? 'status-good' : 'status-neutral'">
              {{ campaign.status === "active" ? "Active" : "Closed" }}
            </span>
          </h4>
          <p class="campaign-raised">
            <strong>{{ pounds(campaign.raised_pence) }}</strong>
            <span v-if="campaign.target_pence"> of {{ pounds(campaign.target_pence) }}</span>
            · {{ campaign.donation_count }} donation{{ campaign.donation_count == 1 ? "" : "s" }}
          </p>
          <p class="campaign-teams">
            <i class="bi bi-people"></i> {{ campaignTeamsLabel(campaign) }}
          </p>
          <div class="progress-track" v-if="campaign.target_pence">
            <div class="progress-fill"
              :style="{ width: progressPercent(campaign) + '%' }"></div>
          </div>
        </div>
      </div>

      <div class="campaign-actions">
        <a class="link-button" :href="campaignPath(campaign)" target="_blank" rel="noopener">
          <i class="bi bi-box-arrow-up-right"></i> View page
        </a>
        <button class="link-button" @click="copyLink(campaign)">
          <i class="bi bi-link-45deg"></i> Copy link
        </button>
        <button class="link-button" @click="toggleQr(campaign)">
          <i class="bi bi-qr-code"></i>
          {{ qrCampaignId === campaign.id ? "Hide QR" : "QR code" }}
        </button>
        <button class="link-button" @click="toggleDonations(campaign)">
          <i class="bi bi-list-ul"></i>
          {{ donationsCampaignId === campaign.id ? "Hide donations" : "Donations" }}
        </button>
        <button
          v-if="campaign.status === 'active'"
          class="link-button danger"
          @click="setStatus(campaign, 'closed')"
        >
          <i class="bi bi-x-circle"></i> Close campaign
        </button>
        <button v-else class="link-button" @click="setStatus(campaign, 'active')">
          <i class="bi bi-arrow-counterclockwise"></i> Reopen
        </button>
      </div>

      <div class="qr-block" v-if="qrCampaignId === campaign.id">
        <img :src="qrDataUrl" alt="QR code linking to the campaign page" />
        <p class="muted">
          Right-click → save image. Scanning opens the campaign page.
        </p>
      </div>

      <table class="donations-table" v-if="donationsCampaignId === campaign.id">
        <thead>
          <tr>
            <th>Date</th>
            <th>Donor</th>
            <th>Email</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="donation in donations" :key="donation.id">
            <td>{{ formatDate(donation.created_at) }}</td>
            <td>
              {{ donation.donor_name || "—" }}
              <span class="muted" v-if="donation.anonymous == 1">(anonymous on page)</span>
            </td>
            <td>{{ donation.donor_email || "—" }}</td>
            <td>{{ pounds(donation.amount_pence) }}</td>
          </tr>
          <tr v-if="donations.length === 0">
            <td colspan="4" class="muted">No donations yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="muted" v-if="message">{{ message }}</p>
  </div>
</template>

<script>
import { getPaymentsCurrency, getCurrencySymbol } from "@/config/tenant";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import { Teams } from "@/services/teams-service.js";
import QRCode from "qrcode";
import SectionIcon from "@/components/SectionIcon/index.vue";

export default {
  name: "ClubFundraising",
  components: { SectionIcon },
  props: {
    clubId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      campaigns: [],
      teams: [],
      showCreateForm: false,
      saving: false,
      formError: "",
      form: {
        title: "",
        description: "",
        targetPounds: null,
        deadline: "",
        suggestedPounds: "5, 10, 25",
        visibility: "all", // 'all' | 'teams'
        selectedTeamIds: [],
      },
      qrCampaignId: null,
      qrDataUrl: "",
      donationsCampaignId: null,
      donations: [],
      message: "",
    };
  },
  created() {
    this.loadCampaigns();
    this.loadTeams();
  },
  computed: {
    currencySymbol() {
      return getCurrencySymbol();
    },
  },
  methods: {
    pounds(amountPence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: getPaymentsCurrency(),
        minimumFractionDigits: amountPence % 100 === 0 ? 0 : 2,
      }).format((amountPence || 0) / 100);
    },
    formatDate(value) {
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date)) return value;
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    progressPercent(campaign) {
      if (!campaign.target_pence) return 0;
      return Math.min(
        100,
        Math.round((campaign.raised_pence / campaign.target_pence) * 100),
      );
    },
    campaignPath(campaign) {
      return `/support/${encodeURIComponent(this.clubId)}/${campaign.id}`;
    },
    campaignUrl(campaign) {
      return window.location.origin + this.campaignPath(campaign);
    },
    async loadCampaigns() {
      this.loading = true;
      try {
        const response = await StripeConnect.listCampaigns(this.clubId);
        this.campaigns = response.data.data || [];
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async loadTeams() {
      try {
        const response = await Teams.find({
          action: "getTeamsDataById",
          clubId: this.clubId,
        });
        const seen = new Set();
        this.teams = (response.data || []).filter((t) => {
          if (seen.has(t.id)) return false;
          seen.add(t.id);
          return true;
        });
      } catch (error) {
        console.log(error);
      }
    },
    campaignTeamsLabel(campaign) {
      const ids = String(campaign.visible_team_ids || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length === 0) return "All teams";
      return ids
        .map((id) => {
          const team = this.teams.find((t) => String(t.id) === id);
          return team ? team.teamName : `Team ${id}`;
        })
        .join(", ");
    },
    async saveCampaign() {
      this.formError = "";
      if (!this.form.title.trim()) {
        this.formError = "Give the campaign a title.";
        return;
      }
      this.saving = true;
      try {
        const suggested = (this.form.suggestedPounds || "")
          .split(",")
          .map((v) => Math.round(parseFloat(v) * 100))
          .filter((v) => v >= 100)
          .join(",");
        await StripeConnect.saveCampaign({
          clubId: this.clubId,
          title: this.form.title,
          description: this.form.description,
          target_pence: this.form.targetPounds
            ? Math.round(parseFloat(this.form.targetPounds) * 100)
            : null,
          deadline: this.form.deadline || null,
          suggested_amounts: suggested,
          // '' = all teams; otherwise a CSV of teams.id the campaign targets
          visible_team_ids:
            this.form.visibility === "teams"
              ? this.form.selectedTeamIds.join(",")
              : "",
        });
        this.form = {
          title: "",
          description: "",
          targetPounds: null,
          deadline: "",
          suggestedPounds: "5, 10, 25",
          visibility: "all",
          selectedTeamIds: [],
        };
        this.showCreateForm = false;
        this.loadCampaigns();
      } catch (error) {
        this.formError =
          error?.response?.data?.error || "Could not save the campaign.";
      } finally {
        this.saving = false;
      }
    },
    async setStatus(campaign, status) {
      if (
        status === "closed" &&
        !window.confirm(`Close "${campaign.title}"? The public page stops taking donations.`)
      ) {
        return;
      }
      try {
        await StripeConnect.setCampaignStatus(this.clubId, campaign.id, status);
        this.loadCampaigns();
      } catch (error) {
        console.log(error);
      }
    },
    async copyLink(campaign) {
      await navigator.clipboard.writeText(this.campaignUrl(campaign));
      this.message = `Link for "${campaign.title}" copied to clipboard.`;
    },
    async toggleQr(campaign) {
      if (this.qrCampaignId === campaign.id) {
        this.qrCampaignId = null;
        return;
      }
      this.qrDataUrl = await QRCode.toDataURL(this.campaignUrl(campaign), {
        width: 240,
        margin: 1,
      });
      this.qrCampaignId = campaign.id;
    },
    async toggleDonations(campaign) {
      if (this.donationsCampaignId === campaign.id) {
        this.donationsCampaignId = null;
        return;
      }
      this.donations = [];
      this.donationsCampaignId = campaign.id;
      try {
        const response = await StripeConnect.getCampaignDonations(
          this.clubId,
          campaign.id,
        );
        this.donations = response.data.data || [];
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.fundraising {
  .card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;

    h3 {
      margin: 0;
    }

    .card-title-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
  }

  .campaign {
    background: #fafbfd;
  }

  .link-button i {
    margin-right: 0.25rem;
  }

  .muted {
    color: #777;
    font-size: 0.9rem;
  }

  .error-text {
    color: #c0392b;
    font-size: 0.85rem;
  }

  .campaign {
    border: 1px solid #e7e9ec;
    border-radius: 8px;
    padding: 1rem;
    margin: 0.75rem 0;

    h4 {
      margin: 0 0 0.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .campaign-raised {
      margin: 0 0 0.4rem;
      color: #444;
      font-size: 0.9rem;
    }
  }

  .progress-track {
    height: 8px;
    border-radius: 999px;
    background: #edf0f3;
    overflow: hidden;
    max-width: 420px;
  }

  .progress-fill {
    height: 100%;
    border-radius: 999px;
    background: var(--brand-color, #1e87f0);
  }

  .campaign-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.6rem;
  }

  .qr-block {
    margin-top: 0.75rem;

    img {
      border: 1px solid #eee;
      border-radius: 6px;
    }
  }

  .donations-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.75rem;
    font-size: 0.85rem;

    th,
    td {
      text-align: left;
      padding: 0.35rem 0.6rem;
      border-bottom: 1px solid #eee;
    }

    th {
      color: #777;
      font-weight: 600;
    }
  }

  .status-pill {
    display: inline-block;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 400;

    &.status-good {
      background: #e6f6ec;
      color: #1e7b3c;
    }
    &.status-neutral {
      background: #eef0f3;
      color: #55606b;
    }
  }

  .link-button {
    background: none;
    border: none;
    color: #1e87f0;
    cursor: pointer;
    padding: 0;
    font-size: 0.85rem;
    text-decoration: none;

    &.danger {
      color: #b03030;
    }
  }

  .action-chip {
    border: 1px solid #ccc;
    background: #fff;
    color: #444;
    border-radius: 6px;
    padding: 0.3rem 0.75rem;
    font-size: 0.8rem;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #e8f2fd;
      border-color: #1e87f0;
      color: #14508f;
    }

    &.confirm {
      background: #1e87f0;
      border-color: #1e87f0;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1a78d6;
        color: #fff;
      }
    }
  }

  .campaign-teams {
    margin: 0.1rem 0 0.4rem;
    color: #55606b;
    font-size: 0.82rem;

    i {
      margin-right: 0.25rem;
    }
  }

  .visibility-block {
    flex-basis: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;

    .vis-title {
      font-weight: 600;
      color: #555;
      font-size: 0.8rem;
    }

    .vis-options {
      display: flex;
      gap: 1.25rem;
    }

    .vis-radio,
    .team-check {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.85rem;
      color: #333;
      cursor: pointer;

      input {
        width: auto;
      }
    }

    .team-checks {
      display: flex;
      flex-wrap: wrap;
      gap: 0.45rem 1.25rem;
      padding: 0.6rem 0.75rem;
      background: #fff;
      border: 1px solid #dbe6f3;
      border-radius: 4px;
    }

    .vis-hint {
      color: #777;
      font-size: 0.8rem;
      margin: 0;
      flex-basis: 100%;
    }
  }

  .inline-form.campaign-form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    margin: 0.75rem 0;
    padding: 0.75rem;
    background: #f4f7fb;
    border: 1px solid #dbe6f3;
    border-radius: 6px;

    label {
      display: flex;
      flex-direction: column;
      font-size: 0.75rem;
      color: #666;
      gap: 0.2rem;

      &.grow {
        flex: 1 1 260px;
      }

      &.full {
        flex-basis: 100%;
      }

      input,
      textarea {
        padding: 0.35rem 0.5rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 0.85rem;
        color: #333;
        background: #fff;
        width: auto;
        font-family: inherit;
      }
    }
  }
}
</style>
